import { Link } from "react-router-dom";
import { Building2, HardHat, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Hero from "@/components/Hero";
import ServiceCard from "@/components/ServiceCard";
import ProjectCard from "@/components/ProjectCard";
import { SERVICES } from "@/data/services";
import { PROJECTS } from "@/data/projects";
import { PAGE_META } from "@/lib/seo-constants";
import { localBusinessSchema, websiteSchema } from "@/lib/jsonld";
import { MAILTO_HREF } from "@/lib/constants";

const STATS = [
  { label: "Years Combined Trade Experience", value: "20+" },
  { label: "Commercial & Industrial Projects Delivered", value: "50+" },
  { label: "GTA-Wide Service", value: "100%" },
];

export default function Home() {
  const meta = PAGE_META.home;

  return (
    <>
      <Seo
        title={meta.title}
        description={meta.description}
        path={meta.path}
        jsonLd={[localBusinessSchema(), websiteSchema()]}
      />

      <Hero
        image="/images/hero-main.jpg"
        imageAlt="Bricklayer mason laying brick veneer on a modern commercial building facade"
        eyebrow="Masonry & Siding Specialists"
        title="Commercial & Industrial Masonry and Siding, Delivered Right"
        subtitle="VCO Group brings skilled bricklaying and siding expertise to commercial and industrial construction — backed by full project management from planning through delivery."
        ctaLabel="Get In Touch"
        ctaHref={MAILTO_HREF}
      />

      <section className="bg-charcoal-light">
        <div className="section-container grid grid-cols-1 gap-8 py-12 text-center sm:grid-cols-3">
          {STATS.map((stat) => (
            <div key={stat.label}>
              <div className="font-display text-4xl font-semibold text-brick-light">
                {stat.value}
              </div>
              <div className="mt-2 text-sm uppercase tracking-wide text-offwhite/70">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="section-container">
          <div className="mx-auto max-w-2xl text-center">
            <p className="font-display text-sm uppercase tracking-[0.3em] text-brick">
              What We Do
            </p>
            <h2 className="mt-3 font-display text-3xl font-semibold text-charcoal sm:text-4xl">
              Trade Expertise, Backed By Full Project Delivery
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
            {SERVICES.map((service) => (
              <ServiceCard key={service.slug} service={service} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="section-container grid grid-cols-1 items-center gap-12 md:grid-cols-2">
          <div>
            <p className="font-display text-sm uppercase tracking-[0.3em] text-brick">
              Why VCO Group
            </p>
            <h2 className="mt-3 font-display text-3xl font-semibold text-charcoal sm:text-4xl">
              Trade Skill Meets Project Discipline
            </h2>
            <p className="mt-4 text-steel">
              Most contractors either manage projects or lay brick — we do both.
              Our crews bring hands-on masonry and siding expertise to every job,
              while our project management team keeps schedules, budgets, and
              trades coordination on track from pre-construction through
              handover.
            </p>
            <div className="mt-8 space-y-5">
              <div className="flex gap-4">
                <HardHat className="mt-1 shrink-0 text-brick" size={24} />
                <div>
                  <h3 className="font-display text-lg font-semibold text-charcoal">
                    Skilled Trade Crews
                  </h3>
                  <p className="text-sm text-steel">
                    Experienced bricklayers and siding installers, not
                    subcontracted out.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Building2 className="mt-1 shrink-0 text-brick" size={24} />
                <div>
                  <h3 className="font-display text-lg font-semibold text-charcoal">
                    Commercial & Industrial Focus
                  </h3>
                  <p className="text-sm text-steel">
                    Built for the scale, code, and scheduling demands of
                    commercial and industrial sites.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <ShieldCheck className="mt-1 shrink-0 text-brick" size={24} />
                <div>
                  <h3 className="font-display text-lg font-semibold text-charcoal">
                    Accountable Delivery
                  </h3>
                  <p className="text-sm text-steel">
                    One team accountable from planning through the final
                    walkthrough.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg shadow-lg">
            <img
              src="/images/service-project-management.jpg"
              alt="Construction project manager reviewing blueprints on a commercial job site"
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="section-container">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div>
              <p className="font-display text-sm uppercase tracking-[0.3em] text-brick">
                Recent Work
              </p>
              <h2 className="mt-3 font-display text-3xl font-semibold text-charcoal sm:text-4xl">
                Featured Projects
              </h2>
            </div>
            <Link
              to="/projects"
              className="font-display text-sm uppercase tracking-wide text-brick hover:text-brick-light"
            >
              View All Projects &rarr;
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {PROJECTS.slice(0, 3).map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-charcoal py-20">
        <div className="section-container text-center">
          <h2 className="font-display text-3xl font-semibold text-offwhite sm:text-4xl">
            Ready To Start Your Next Project?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-offwhite/70">
            Tell us about your commercial or industrial masonry, siding, or
            construction management needs.
          </p>
          <a
            href={MAILTO_HREF}
            className="mt-8 inline-block rounded bg-brick px-8 py-4 font-display text-base uppercase tracking-wide text-offwhite transition-colors hover:bg-brick-light"
          >
            Email Us
          </a>
        </div>
      </section>
    </>
  );
}
